import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ShoppingCartIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  HeartIcon,
  UserIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { cartAPI } from '../services/api';
import { useChatUnread } from '../hooks/useChatUnread';

const MobileFooter = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthenticated } = useAuth();
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const chatUnread = useChatUnread();

  const isActive = (path) => pathname === path || (path !== '/' && pathname.startsWith(path));

  const bubbleClasses =
    'h-14 w-14 rounded-full bg-white text-kadolar-700 shadow-xl ring-4 ring-kadolar-300 flex items-center justify-center -mt-8 active:scale-95 transition';
  const iconClasses = 'h-8 w-8 drop-shadow-sm';

  const loadCartCount = useMemo(() => {
    return async () => {
      if (!isAuthenticated) {
        try {
          const mod = await import('../utils/guestCart');
          setCartItemsCount(mod.getGuestCartCount());
        } catch {
          setCartItemsCount(0);
        }
        return;
      }
      try {
        const response = await cartAPI.getCart();
        const count = response?.data?.items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;
        setCartItemsCount(count);
      } catch (_) {}
    };
  }, [isAuthenticated]);

  useEffect(() => {
    loadCartCount();
  }, [loadCartCount, pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onCartChanged = () => loadCartCount();
    window.addEventListener('kadolar:cart-changed', onCartChanged);
    return () => window.removeEventListener('kadolar:cart-changed', onCartChanged);
  }, [loadCartCount]);

  return (
    <nav
      role="navigation"
      aria-label="پابرگ موبایل"
      className="fixed bottom-0 inset-x-0 z-40 text-white bg-gradient-to-r from-kadolar-900 via-kadolar-800 to-kadolar-900 shadow-[0_-6px_16px_rgba(0,0,0,0.18)] border-t border-kadolar-700 lg:hidden"
    >
      
      <div className="mx-auto max-w-4xl px-6 pb-[env(safe-area-inset-bottom)]">
        <div className="h-16 flex items-center justify-between">
          {/* Messages */}
          <NavLink
            to="/chats"
            aria-label="پیامها"
            className={() => `flex flex-col items-center ${isActive('/chats') || isActive('/chat') ? 'text-white' : 'text-white/95'} hover:text-white`}
          >
            {isActive('/chats') || isActive('/chat') ? (
              <span className={`${bubbleClasses} relative`}>
                <ChatBubbleLeftRightIcon className="h-8 w-8" />
                {chatUnread > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-xl animate-bounce">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </span>
            ) : (
              <span className="relative">
                <ChatBubbleLeftRightIcon className={iconClasses} />
                {chatUnread > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-xl animate-bounce">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </span>
            )}
          </NavLink>

          {/* Cart */}
          <NavLink
            to="/cart"
            aria-label="سبد خرید"
            className={() => `flex flex-col items-center ${isActive('/cart') ? 'text-white' : 'text-white/95'} hover:text-white`}
          >
            {isActive('/cart') ? (
              <span className={`${bubbleClasses} relative`}>
                <ShoppingCartIcon className="h-8 w-8" />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-700 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-xl animate-bounce">
                    {cartItemsCount}
                  </span>
                )}
              </span>
            ) : (
              <span className="relative">
                <ShoppingCartIcon className={iconClasses} />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-700 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-xl animate-bounce">
                    {cartItemsCount}
                  </span>
                )}
              </span>
            )}
          </NavLink>

          {/* Home */}
          <NavLink
            to="/"
            aria-label="خانه"
            className={() => `flex flex-col items-center ${pathname === '/' ? 'text-white' : 'text-white/95'} hover:text-white`}
          >
            {pathname === '/' ? (
              <span className={bubbleClasses}>
                <HomeIcon className="h-8 w-8" />
              </span>
            ) : (
              <HomeIcon className={iconClasses} />
            )}
          </NavLink>

          {/* Wishlist */}
          <NavLink
            to="/wishlist"
            aria-label="علاقه‌مندی‌ها"
            className={() => `flex flex-col items-center ${isActive('/wishlist') ? 'text-white' : 'text-white/95'} hover:text-white`}
          >
            {isActive('/wishlist') ? (
              <span className={bubbleClasses}>
                <HeartIcon className="h-8 w-8" />
              </span>
            ) : (
              <HeartIcon className={iconClasses} />
            )}
          </NavLink>

          {/* Profile */}
          <NavLink
            to="/profile"
            aria-label="پروفایل"
            className={() => `flex flex-col items-center ${isActive('/profile') ? 'text-white' : 'text-white/95'} hover:text-white`}
          >
            {isActive('/profile') ? (
              <span className={bubbleClasses}>
                <UserIcon className="h-8 w-8" />
              </span>
            ) : (
              <UserIcon className={iconClasses} />
            )}
          </NavLink>
        </div>
      </div>
    </nav>
  );
};

export default MobileFooter;
